import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { UseMutationsParams, AlertSeverityEnum } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { IOrdenesCompraPagingRes, OrdenesCompra } from '@/shared/interfaces/erp/compras/ordenes-compra.interface';


const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchOrdenesCompras = (params?: GetOrdenesComprasParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['orden-compra', ...Object.values(params || {})],
    queryFn: async () => {
      return getOrdenesCompras(params)
    },
    enabled
  })
} 
/* export const useFetchOrdenesCompras = ({
  page = 1,
  page_size = 10,
  concepto = '',
  numero_referencia = '',
  estado_orden = '',
  id_orden_compra = ''
  //url_pdf=''
}: GetOrdenesComprasParams) => {
  return useQuery({
    queryKey: ['orden-compra', page, concepto, estado_orden, page_size,id_orden_compra],
    queryFn: async () => {
      return getOrdenesCompras({ page, concepto, estado_orden, page_size, id_orden_compra
      });
    },
  });
}; */

export const useGetOrdenesCompra = (id: string) => {
  return useQuery({
    queryKey: ['orden-compra', id],
    queryFn: async () => {
      return getOrdenesCompra(id);
    },
    enabled: !!id
  });
};

export const useCreateOrdenesCompra = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createOrdenesCompra,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orden-compra'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Orden de compra creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateOrdenesCompra = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateOrdenesCompra,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orden-compra'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Orden de compra actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemoveOrdenesCompra = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;
  return useMutation({
    mutationFn: deleteOrdenesCompra,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orden-compra'] })

    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);

    },
    onError: (error) => {
      handleAxiosError(error)
    }
  })
}

///* axios
export interface GetOrdenesComprasParams extends Partial<OrdenesCompra> {
  page?: number;
  page_size?: number;
  concepto?: string;
  numero_referencia?: string;
  solicitud_ingreso?: string;
  estado_orden?: string;
  id_orden_compra?: string;
  estado_orden_in?: string

  //url_pdf?:string;
};

export type CreateOrdenesCompraParams = Omit<OrdenesCompra, 'id_orden_compra'>;
export type UpdateOrdenesCompraParams = {
  id: string;
  data: CreateOrdenesCompraParams;
  skipLoading?: boolean // permite activar o desactibvar isloading
};

export const getOrdenesCompras = async (params?: GetOrdenesComprasParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<IOrdenesCompraPagingRes>(
    `${urlBackend}api/v1/orden-compra/?${queryParams}`,
    true
  );
};

export const getOrdenesCompra = async (id: string) => {
  try {
    return await get<OrdenesCompra>(`${urlBackend}api/v1/orden-compra/${id}/`, true);
  }
  catch (error) {
    return handleAxiosError(error);
  }
};

export const createOrdenesCompra = async (data: CreateOrdenesCompraParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<OrdenesCompra>(`${urlBackend}api/v1/orden-compra/`, data, true);
};

export const updateOrdenesCompra = async ({ id, data, skipLoading }: UpdateOrdenesCompraParams) => {
  if (!skipLoading) {
    const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
    setIsGlobalLoading(true);
  }
  return put<OrdenesCompra>(`${urlBackend}api/v1/orden-compra/${id}/`, data, true);
};

export const deleteOrdenesCompra = async ({ id, data }: UpdateOrdenesCompraParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<OrdenesCompra>(`${urlBackend}api/v1/orden-compras/${id}/`, data, true);
  setAlert(true, res.message, res.status)
  return res;
}
